import React from "react"
import { useEffect } from "react"
import { IEnkLoggedInUser } from "./auth-interfaces"
import { EnkIWPServerAuth as ApiAuth } from "../api/api-iwp-server"
import { loginResultToUser } from "../api/api-utils"
import AuthContext from "./auth-context"

interface AuthProviderProps {
  children: React.ReactNode
}

// Key to store current group in session storage
export const CURRENTGROUP_ENTRY = "IWP_currentGroupId"

export interface IEnkLoginResult {
  mfaNeeded: boolean
  mfaSetupRequired?: boolean
  user?: IEnkLoggedInUser
}

export interface IEnkAuthContextType {
  user: IEnkLoggedInUser | null
  loading: boolean
  onUserInfoChanged: (user: IEnkLoggedInUser) => void
  onLoggedIn: (user: IEnkLoggedInUser, rememberUser: boolean) => void
  onLogout: () => void
  onGroupChanged: (groupId: number) => void
  getCurrentGroupId: () => number
}

export const EnkAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [loggedInUser, setLoggedInUser] = React.useState<IEnkLoggedInUser | null>(null)
  const [loading, setLoading] = React.useState<boolean>(true)

  const setCurrentGroupId = (groupId: number) => {
    sessionStorage.setItem(CURRENTGROUP_ENTRY, groupId.toString())
  }

  const getCurrentGroupId = (): number => {
    // Get current group from session storage
    const currentgroup = sessionStorage.getItem(CURRENTGROUP_ENTRY)
    return currentgroup !== null && !isNaN(Number(currentgroup)) ? Number(currentgroup) : -1
  }

  const doSilentLogin = async () => {
    //Call api to get user info
    try {
      const loginResult = await ApiAuth.silentLogin(getCurrentGroupId())
      if (loginResult.user) {
        const newLoggedInUser = loginResultToUser(loginResult.user)
        setLoggedInUser(newLoggedInUser)
        setLoading(false)
        setCurrentGroupId(newLoggedInUser?.currentGroupId || -1)
      } else {
        throw new Error("User not found")
      }
    } catch (error: unknown) {
      console.error("Silent login failed", error)
      setLoading(false)
      setLoggedInUser(null)
      setCurrentGroupId(-1)
    }
  }
  useEffect(() => {
    //call silentLogin when Auth is loading
    void doSilentLogin()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLoggedIn = (user: IEnkLoggedInUser) => {
    setLoggedInUser(user)
    setCurrentGroupId(user.currentGroupId || -1)
  }

  const handleLogout = () => {
    setLoggedInUser(null)
    //Call logout api to clear the cookie
    void ApiAuth.logout()
  }

  const handleUserChanged = (user: IEnkLoggedInUser) => {
    setLoggedInUser(user)
  }

  const handleGroupChanged = (groupId: number) => {
    setCurrentGroupId(groupId)
  }

  const value: IEnkAuthContextType = {
    user: loggedInUser,
    loading,
    onLoggedIn: handleLoggedIn,
    onLogout: handleLogout,
    onUserInfoChanged: handleUserChanged,
    onGroupChanged: handleGroupChanged,
    getCurrentGroupId: getCurrentGroupId,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
